import * as React from 'react'
import { NumericFormat, NumericFormatProps } from 'react-number-format'
import TextField from '@mui/material/TextField'

import { TextFieldProps } from '@mui/material'

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: string
  unitType?: string
}

// The amount and weight boxes are two views of ONE number, each rendered from state
// the other one writes. react-number-format fires onValueChange for PROGRAMMATIC value
// changes as well as typing, so typing into one box changed the other box's `value`
// prop, which fired ITS handler, which wrote back over what was just typed — the
// customer's amount visibly changing as they entered it (Greg, 8-11 Aug).
//
// sourceInfo.source is 'event' for real input and 'prop' for a re-render, so only real
// input is forwarded. This is the loop cut at its source; the numeric guards in
// TradeProduct are no longer load-bearing (and could not hold anyway —
// 1.001g * 1000 === 1000.9999999999999, so an equality guard fails for ~2% of
// milligram values).
const NumericFormatAmount = React.forwardRef<NumericFormatProps, CustomProps>(
  function NumericFormatCustom(props, ref) {
    const { onChange, ...other } = props

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values, sourceInfo) => {
          if (sourceInfo.source !== 'event') return
          onChange({
            target: {
              name: props.name,
              value: values.value
            }
          })
        }}
        thousandSeparator
        valueIsNumericString
        // Money has two decimal places. Without this the box accepted "$100.5501"
        // while the order quoted $100.55 — the field showing more precision than the
        // thing it is buying. Nothing is rounded away: the customer simply cannot
        // type a fraction of a cent.
        decimalScale={2}
        prefix='Edit: $'
      />
    )
  }
)

export const NumericFormatWeight = React.forwardRef<
  NumericFormatProps,
  CustomProps
>(function NumericFormatCustom(props, ref) {
  const { onChange, ...other } = props

  return (
    <NumericFormat
      {...other}
      onChange={undefined}
      getInputRef={ref}
      onValueChange={(values, sourceInfo) => {
        if (sourceInfo.source !== 'event') return
        onChange({
          target: {
            name: props.name,
            value: values.value
          }
        })
      }}
      thousandSeparator
      valueIsNumericString
      decimalScale={3}
      suffix=' g'
    />
  )
})

type FormattedInputsProps = TextFieldProps & { unitType?: string }

export default function FormattedInputs(props: FormattedInputsProps) {
  const { unitType = 'mg', ...textFieldProps } = props
  return (
    <TextField
      {...textFieldProps}
      InputProps={{
        inputComponent: (textFieldProps?.name === 'amount'
          ? NumericFormatAmount
          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
            NumericFormatWeight) as any,
        inputProps: { unitType }
      }}
      sx={{
        border: 'none',
        '& fieldset': { display: 'none' },
        '& input': {
          p: 0,
          borderRadius: 0,
          color: 'primary.main',
          pl: 1,
          pr: '2px',
          bgcolor: 'transparent',
          textAlign: 'right'
        }
      }}
      variant='outlined'
    />
  )
}
